import React, { useCallback, useState } from 'react';
import classnames from 'clsx';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { FloatTextArea } from 'ui-components/components/float-text-area';
import { Input, version2 } from 'ui-components/components/input';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';

import { messages } from './messages';
import style from './style.module.css';
import { SimpleModal } from '../../../components/simple-modal';
import { PickupContactPropType } from '../../../prop-types/pickup-contact';

export const ShipFromContactModal = ({
  onSubmit,
  onCancel,
  onClose,
  showDefaultLocationNotes = false,
  isSaving = false,
  errorCollection = emptyArray,
  contact = emptyObject,
}) => {
  const { firstName: selectedFirstName, lastName: selectedLastName } = contact;
  const [fullName, setFullName] = useState(selectedFirstName && selectedLastName ? `${selectedFirstName} ${selectedLastName}` : '');
  const [emailAddress, setEmailAddress] = useState(contact.emailAddress || '');
  const [phoneNumber, setPhoneNumber] = useState(contact.phoneNumber || '');
  const [phoneNumberExtension, setPhoneNumberExtension] = useState(contact.phoneNumberExtension || '');
  const [locationNotes, setLocationNotes] = useState(contact.locationNotes || '');
  const intl = useIntl();

  const onAction = useCallback(() => {
    const [firstName = '', ...lastName] = fullName.split(' ');
    const newContact = {
      firstName,
      lastName: lastName ? lastName.join(' ') : '',
      emailAddress,
      phoneNumber,
      phoneNumberExtension,
      locationNotes,
    };
    onSubmit(newContact);
  }, [emailAddress, fullName, locationNotes, onSubmit, phoneNumber, phoneNumberExtension]);

  const extractFullNameFromEvent = useCallback((event) => {
    const fullNameValue = event?.target?.value;
    setFullName(fullNameValue);
  }, []);
  const extractEmailFromEvent = useCallback((event) => {
    const emailValue = event?.target?.value;
    setEmailAddress(emailValue);
  }, []);
  const extractPhoneNumberFromEvent = useCallback((event) => {
    const phoneValue = event?.target?.value;
    setPhoneNumber(phoneValue);
  }, []);
  const extractPhoneExtensionFromEvent = useCallback((event) => {
    const phoneValue = event?.target?.value;
    setPhoneNumberExtension(phoneValue);
  }, []);

  return (
    <SimpleModal
      actionMessage={messages.createContact}
      cancelMessage={messages.cancel}
      data-testid='shipfrom-contact-modal'
      headerMessage={messages.header}
      isActionDisabled={isSaving}
      onAction={onAction}
      onCancel={onCancel}
      onClose={onClose}
    >
      {isSaving ? (
        <div className={style['loading-indicator-container']}>
          <LoadingIndicator className={style['loading-indicator']} />
        </div>
      ) : (
        <div className={style['modal-body-content']}>
          {errorCollection.map((message, index) => (
            <BannerMessageV2 key={index} content={message} status={BannerStatus.Error} />
          ))}
          <div className={style['contact-description-text']}>
            <FormattedMessage {...messages.contactDescription} />
          </div>
          <Input
            aria-label='ship-from-contact-full-name'
            className={style.input}
            label={intl.formatMessage(messages.fullName)}
            name='shipFromContactFullName'
            onChange={extractFullNameFromEvent}
            required
            value={fullName}
            version={version2}
          />
          <Input
            aria-label='ship-from-contact-email'
            className={style.input}
            label={intl.formatMessage(messages.email)}
            name='shipFromContactEmail'
            onChange={extractEmailFromEvent}
            required
            type='email'
            value={emailAddress}
            version={version2}
          />
          <div className={style['inline-input-wrapper']}>
            <Input
              aria-label='ship-from-contact-phone-number'
              className={classnames(style.input, style['phone-input'])}
              label={intl.formatMessage(messages.phoneNumber)}
              name='shipFromContactPhoneNumber'
              onChange={extractPhoneNumberFromEvent}
              placeholder='1 (555) 555-5555'
              required
              type='tel'
              value={phoneNumber}
              version={version2}
            />
          </div>
          <div className={style['inline-input-wrapper']}>
            <Input
              aria-label='ship-from-contact-extension'
              className={classnames(style.input, style['phone-input'])}
              label={intl.formatMessage(messages.extension, {
                span: (chunks) => <span className={style.subtext}>{chunks}</span>,
              })}
              name='shipFromContactExtension'
              onChange={extractPhoneExtensionFromEvent}
              type='tel'
              value={phoneNumberExtension}
              version={version2}
            />
          </div>
          <br />
          {showDefaultLocationNotes ? (
            <>
              <label className={style.label}>
                <FormattedMessage {...messages.defaultLocationNotes} />
              </label>
              <FloatTextArea className={style['location-description']} maxLength={250} onChange={setLocationNotes} value={locationNotes} />
            </>
          ) : null}
        </div>
      )}
    </SimpleModal>
  );
};

ShipFromContactModal.propTypes = {
  contact: PickupContactPropType,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  showDefaultLocationNotes: PropTypes.bool,
  isSaving: PropTypes.bool,
  errorCollection: PropTypes.array,
};
